import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import GlobeIcon from '@atlaskit/icon/core/globe';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { ElementName, Card as SmartCard, SmartLinkSize, TitleBlock } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { messages } from '../../messages';

const styles = cssMap({
	link: {
		textDecoration: 'none',
		color: token('color.link'),
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
			textDecoration: 'underline',
		},
		'&:focus': {
			color: token('color.link'),
			textDecoration: 'underline',
		},
		'&:visited': {
			color: token('color.link'),
		},
	},
	iconContainer: {
		paddingTop: token('space.025'),
	},
});

const FallbackComponent = ({
	url,
	onClick,
}: {
	url: string;
	onClick: ({
		event,
		resourceUrl,
		location,
	}: {
		event: React.MouseEvent | React.KeyboardEvent;
		resourceUrl: string;
		location: string;
	}) => void;
}) => {
	const { formatMessage } = useIntl();

	return (
		<Anchor
			href={url}
			target="_blank"
			xcss={styles.link}
			onClick={(e) => onClick({ event: e, resourceUrl: url, location: 'related-link' })}
		>
			<Inline alignBlock="start" space="space.100" alignInline="start">
				<Box xcss={styles.iconContainer}>
					<GlobeIcon label={formatMessage(messages.globe_icon_label)} size="medium" />
				</Box>
				<Stack space="space.050">
					<Text size="medium" maxLines={1} color="color.link">
						{url}
					</Text>
					<Text size="small" maxLines={1} color="color.text.subtle">
						{formatMessage(messages.webpage_label)}
					</Text>
				</Stack>
			</Inline>
		</Anchor>
	);
};

export const LinkCard = ({
	url,
	onClick,
}: {
	url: string;
	onClick: ({
		event,
		resourceUrl,
		location,
	}: {
		event: React.MouseEvent | React.KeyboardEvent;
		resourceUrl: string;
		location: string;
	}) => void;
}): React.JSX.Element => {
	const [isReady, setIsReady] = useState(false);
	const [forceFallback, setForceFallback] = useState(false);

	return (
		<Box>
			{/* We're using css to hide/show the loading state as a workaround because SmartCard component does not support loading states */}
			{/* PS: the smart card still has to be in the dom for it to work, do not use conditional logic to render it */}
			<div
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					transition: 'visibility 0.3s ease-in-out, opacity 0.3s ease-in-out',
					visibility: isReady ? 'hidden' : 'visible',
					opacity: isReady ? 0 : 1,
					pointerEvents: isReady ? 'none' : 'auto',
					height: isReady ? 0 : 'auto',
					width: isReady ? 0 : '100%',
				}}
			>
				<Skeleton height={32} width={200} isShimmering />
			</div>
			<div
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					transition: 'visibility 0.3s ease-in-out, opacity 0.3s ease-in-out',
					visibility: isReady ? 'visible' : 'hidden',
					opacity: isReady ? 1 : 0,
					pointerEvents: isReady ? 'auto' : 'none',
					height: isReady ? 'auto' : 0,
					width: isReady ? '100%' : 0,
				}}
			>
				{/* we force the fallback to be used because smart cards render a different icon than we need when it can't resolve the link */}
				{forceFallback ? (
					<FallbackComponent url={url} onClick={onClick} />
				) : (
					<SmartCard
						url={url}
						ui={{
							removeBlockRestriction: true,
							hidePadding: true,
							hideElevation: true,
							hideBackground: true,
						}}
						appearance="block"
						onClick={(e) => {
							onClick({ event: e, resourceUrl: url, location: 'related-link' });
						}}
						onResolve={() => setIsReady(true)}
						onError={() => {
							setForceFallback(true);
							setIsReady(true);
						}}
						fallbackComponent={() => <FallbackComponent url={url} onClick={onClick} />}
					>
						<TitleBlock
							size={SmartLinkSize.Medium}
							subtitle={[{ name: ElementName.ModifiedOn }]}
							maxLines={1}
						/>
					</SmartCard>
				)}
			</div>
		</Box>
	);
};
